import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, Loader2, ArrowRight, CheckCircle2, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const PROJECT_TYPES = ["Residential", "Commercial", "Hospitality", "Retail", "Other"];

const PRODUCT_INTEREST_OPTIONS = [
  "Engineered Hardwood",
  "Laminate Planks",
  "Luxury Vinyl Tiles",
  "Royal Wallpapers",
  "Geometric Wallpapers",
  "Custom Wallpapers",
  "Broadloom Carpets",
  "Carpet Tiles",
  "Door Mats",
  "Artificial Turf",
  "Doormat Rolls",
  "Balcony Accents",
];

const initialState = {
  name: "",
  email: "",
  mobile: "",
  project_type: PROJECT_TYPES[0],
  product_interest: [],
  quantity: "",
  message: "",
};

export default function QuoteModal({ open, onClose, defaultCategory = "", defaultInterest = [], source = "" }) {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // Reset state when opened
  useEffect(() => {
    if (open) {
      setForm({ ...initialState, product_interest: defaultInterest || [] });
      setError("");
      setSuccess(false);
      // Lock body scroll
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
    // defaultInterest is an array literal from parent — comparing by reference would
    // reset the form on every parent render. Intentionally keying only on `open`.
  }, [open]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleInterest = (item) => {
    setForm((f) => ({
      ...f,
      product_interest: f.product_interest.includes(item)
        ? f.product_interest.filter((i) => i !== item)
        : [...f.product_interest, item],
    }));
  };

  const validate = () => {
    if (form.name.trim().length < 2) return "Please enter your name.";
    if (!/^[0-9+\-\s()]{7,}$/.test(form.mobile.trim())) return "Please enter a valid mobile number.";
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email.trim())) return "Please enter a valid email or leave it blank.";
    if (form.quantity.trim().length < 1) return "Please enter project size (sq. ft.).";
    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        mobile: form.mobile.trim(),
        product_category: defaultCategory || (form.product_interest[0] || "General Inquiry"),
        project_type: form.project_type,
        product_interest: form.product_interest,
        quantity: form.quantity.trim(),
        message: form.message.trim(),
        source: source || "Product Card Quote Modal",
      };
      await axios.post(`${API}/leads`, payload);
      setSuccess(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please call us at 098450 48406.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center px-4 py-6 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      data-testid="quote-modal"
    >
      <div className="absolute inset-0 bg-slate-950/85 backdrop-blur-md" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white border border-slate-200 shadow-2xl animate-fade-up my-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 h-10 w-10 inline-flex items-center justify-center text-slate-500 hover:text-amber-700 transition-colors z-10"
          aria-label="Close"
          data-testid="quote-modal-close"
        >
          <X className="h-5 w-5" />
        </button>

        {success ? (
          <div className="p-8 md:p-12 text-center" data-testid="quote-modal-success">
            <span className="inline-flex h-14 w-14 items-center justify-center bg-emerald-50 border border-emerald-200 text-emerald-600 mb-5">
              <CheckCircle2 className="h-6 w-6" />
            </span>
            <p className="eyebrow">Sent Successfully</p>
            <h3 className="font-display text-3xl md:text-4xl text-slate-900 leading-tight mt-3">Quote request sent!</h3>
            <p className="mt-4 text-slate-600 leading-relaxed max-w-md mx-auto">
              Our wholesale desk will contact you shortly with a tailored price list and product spec sheets.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="https://wa.me/919845048406"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-3 text-[11px] tracking-[0.22em] uppercase"
              >
                Chat on WhatsApp
                <ArrowRight className="h-4 w-4" />
              </a>
              <button
                onClick={onClose}
                className="inline-flex items-center justify-center gap-2 border border-slate-300 hover:border-slate-900 text-slate-900 px-5 py-3 text-[11px] tracking-[0.22em] uppercase"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="p-7 md:p-10" data-testid="quote-modal-form" noValidate>
            <div className="mb-7">
              <p className="eyebrow !text-amber-700">Bulk Inquiry</p>
              <h3 className="font-display text-3xl md:text-4xl text-slate-900 leading-tight mt-2">
                Request Your Wholesale Price List
              </h3>
              {defaultCategory && (
                <p className="mt-3 inline-flex items-center gap-2 text-xs text-slate-500">
                  <Sparkles className="h-3.5 w-3.5 text-amber-700" />
                  Pre-selected: <span className="text-slate-900 font-medium">{defaultCategory}</span>
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Full Name *" name="name" value={form.name} onChange={(v) => update("name", v)} placeholder="e.g. Arjun Mehta" testid="quote-input-name" />
              <Field label="Email" name="email" value={form.email} onChange={(v) => update("email", v)} placeholder="you@studio.com" testid="quote-input-email" type="email" />
              <Field label="Phone *" name="mobile" value={form.mobile} onChange={(v) => update("mobile", v)} placeholder="+91 98450 48406" testid="quote-input-mobile" />
              <div>
                <Label>Project Type *</Label>
                <select
                  value={form.project_type}
                  onChange={(e) => update("project_type", e.target.value)}
                  className="w-full bg-transparent border-b border-slate-300 focus:border-amber-700 outline-none py-3 text-slate-900 text-sm"
                  data-testid="quote-select-project-type"
                >
                  {PROJECT_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <Label>Product Interest (select all that apply)</Label>
                <div className="flex flex-wrap gap-2 mt-2" data-testid="quote-product-interest">
                  {PRODUCT_INTEREST_OPTIONS.map((opt) => {
                    const selected = form.product_interest.includes(opt);
                    return (
                      <button
                        type="button"
                        key={opt}
                        onClick={() => toggleInterest(opt)}
                        className={`text-[11px] tracking-[0.05em] px-3 py-2 border transition-colors ${
                          selected
                            ? "bg-amber-700 border-amber-700 text-white"
                            : "bg-white border-slate-300 text-slate-700 hover:border-amber-700 hover:text-amber-700"
                        }`}
                        data-testid={`quote-interest-${opt.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
              <Field label="Project Size (Sq. Ft.) *" name="quantity" value={form.quantity} onChange={(v) => update("quantity", v)} placeholder="e.g. 2500" testid="quote-input-quantity" />
              <div className="md:col-span-2">
                <Label>Message (optional)</Label>
                <textarea
                  rows={3}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder="Tell us about your site, timelines or preferred brands…"
                  className="w-full bg-transparent border-b border-slate-300 focus:border-amber-700 outline-none py-3 text-slate-900 text-sm resize-none"
                  data-testid="quote-input-message"
                />
              </div>
            </div>

            {error && (
              <p className="mt-5 text-sm text-red-600" data-testid="quote-modal-error">{error}</p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="mt-7 w-full glow-amber bg-amber-700 hover:bg-amber-800 disabled:opacity-70 text-white px-6 py-4 text-[12px] tracking-[0.24em] uppercase inline-flex items-center justify-center gap-3 transition-colors"
              data-testid="quote-modal-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                </>
              ) : (
                <>
                  Send My Quote Request <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
            <p className="mt-4 text-xs text-slate-500">
              By submitting, you agree to be contacted by Shri Gurunath Enterprises about your project enquiry. We never spam.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

function Label({ children }) {
  return <span className="block eyebrow !text-slate-500 mb-2">{children}</span>;
}

function Field({ label, name, value, onChange, placeholder, testid, type = "text" }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-transparent border-b border-slate-300 focus:border-amber-700 outline-none py-3 text-slate-900 text-sm placeholder:text-slate-400"
        data-testid={testid}
      />
    </div>
  );
}
